import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useLocation } from "react-router-dom";
import { api } from "../api/client";
import IssueCard from "../components/IssueCard";
import LoadingSpinner from "../components/LoadingSpinner";

const PAGE_SIZE = 100;

// Static option lists — the page filters server-side now, so we can't rely on
// the loaded page to enumerate every possible value.
const SEVERITY_OPTIONS = ["critical", "high", "warning", "medium", "low", "info"];
const STATUS_OPTIONS = ["open", "reopened", "new", "actioned", "resolved", "false_positive"];

function Issues() {
    const { accounts } = useMsal();
    const loc = useLocation();
    const currentEmail = accounts[0]?.username || "";

    // Sidebar nav drives the base status group (e.g. /issues?status=open); the
    // dashboard "Outstanding" drill-through pre-seeds siteId/assignee/rpl/state.
    const urlParams = new URLSearchParams(loc.search);
    const urlStatus = urlParams.get("status") || "";

    // --- Server-side filters ---
    const [filterSite, setFilterSite] = useState(urlParams.get("siteId") || urlParams.get("q") || "");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [filterSeverity, setFilterSeverity] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterTeam, setFilterTeam] = useState(urlParams.get("assignee") || "");
    const [filterRpl, setFilterRpl] = useState(urlParams.get("rpl") || "");
    const [filterState, setFilterState] = useState(urlParams.get("state") || "");
    const [filterFixture, setFilterFixture] = useState("");
    const [filterSource, setFilterSource] = useState("");
    const [myIssuesOnly, setMyIssuesOnly] = useState(false);
    const [sortBy, setSortBy] = useState("newest");
    const [showResolved, setShowResolved] = useState(urlStatus === "resolved");

    // --- Display-only (client-side) ---
    const [groupBy, setGroupBy] = useState("none");

    // Full RPL list for the filter dropdown (loaded once, from RplMapping).
    const [rplOptions, setRplOptions] = useState([]);

    // --- Data / paging ---
    const [issues, setIssues] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [counts, setCounts] = useState({ open: 0, actioned: 0, resolved: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (loading || !loc.hash) return;
        const id = decodeURIComponent(loc.hash.replace(/^#/, ""));
        const t = setTimeout(() => {
            const el = document.getElementById(id);
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.style.outline = "3px solid #3b82f6";
            el.style.outlineOffset = "2px";
        }, 250);
        return () => clearTimeout(t);
    }, [loading, loc.hash, issues]);

    // Build the server query params from the current filter state.
    const buildParams = useCallback((offset) => {
        const p = {};
        // Status: explicit dropdown wins; otherwise the sidebar group; otherwise default working set.
        if (filterStatus) {
            p.status = filterStatus;
        } else if (urlStatus) {
            p.statusGroup = urlStatus;
        } else if (showResolved) {
            p.statusGroup = "all";
        }
        if (filterSite) {
            const site = filterSite.trim();
            if (/^\d+$/.test(site)) p.siteId = site;
            else p.q = site;
        }
        if (fromDate) p.from = fromDate;
        if (toDate) p.to = toDate;
        if (filterSeverity) p.severity = filterSeverity;
        if (filterTeam) p.assignee = filterTeam;
        if (filterRpl) p.rpl = filterRpl;
        if (filterState) p.state = filterState;
        if (filterFixture) p.fixtureType = filterFixture;
        if (filterSource) p.source = filterSource;
        if (myIssuesOnly && currentEmail) p.assignedBy = currentEmail;
        if (sortBy !== "newest") p.orderBy = sortBy;
        p.limit = PAGE_SIZE;
        p.offset = offset;
        return p;
    }, [filterStatus, urlStatus, showResolved, filterSite, fromDate, toDate,
        filterSeverity, filterTeam, filterRpl, filterState, filterFixture, filterSource, myIssuesOnly, currentEmail, sortBy]);

    // Load page 1 (replaces the list). Debounced via the effect below.
    const loadFirstPage = useCallback(async () => {
        setLoading(true);
        try {
            const qs = "?" + new URLSearchParams(buildParams(0)).toString();
            const res = await api.allIssues(qs);
            setIssues(res.issues || []);
            setTotalCount(res.totalCount || 0);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [buildParams]);

    // Load the next page and append.
    const loadMore = useCallback(async () => {
        setLoadingMore(true);
        try {
            const qs = "?" + new URLSearchParams(buildParams(issues.length)).toString();
            const res = await api.allIssues(qs);
            setIssues((prev) => [...prev, ...(res.issues || [])]);
            setTotalCount(res.totalCount || 0);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingMore(false);
        }
    }, [buildParams, issues.length]);

    // Header counts — overall (or site-scoped), independent of the row filters.
    const loadCounts = useCallback(async () => {
        try {
            const qs = filterSite ? "?" + new URLSearchParams({ siteId: filterSite.trim() }).toString() : "";
            const res = await api.issueCount(qs);
            setCounts(res || { open: 0, actioned: 0, resolved: 0, total: 0 });
        } catch {
            /* counts are best-effort */
        }
    }, [filterSite]);

    // Re-query page 1 whenever any server filter changes. Debounced so typing in
    // the site box / picking dates doesn't fire a query per keystroke.
    const firstRun = useRef(true);
    useEffect(() => {
        const delay = firstRun.current ? 0 : 350;
        firstRun.current = false;
        const t = setTimeout(() => { loadFirstPage(); loadCounts(); }, delay);
        return () => clearTimeout(t);
    }, [loadFirstPage, loadCounts]);

    // Keep base filter in sync when the sidebar link changes
    useEffect(() => {
        setShowResolved(urlStatus === "resolved");
        setFilterStatus("");
    }, [urlStatus]);

    // Load the full RPL list once for the filter dropdown.
    useEffect(() => {
        api.issueRplList().then(r => setRplOptions(r.rpls || [])).catch(() => {});
    }, []);

    const hasMore = issues.length < totalCount;

    // Dropdown options for team/fixture — best-effort from the loaded page,
    // plus the currently selected value so it never vanishes.
    const teams = useMemo(() => {
        const s = new Set(issues.map(i => i.assignedToTeam).filter(Boolean));
        if (filterTeam) s.add(filterTeam);
        return [...s].sort();
    }, [issues, filterTeam]);
    const fixtures = useMemo(() => {
        const s = new Set(issues.map(i => i.fixtureType).filter(Boolean));
        if (filterFixture) s.add(filterFixture);
        return [...s].sort();
    }, [issues, filterFixture]);

    // Grouping is display-only, over the rows currently loaded.
    const grouped = useMemo(() => {
        if (groupBy === "none") return { "": issues };
        const groups = {};
        for (const issue of issues) {
            let key;
            if (groupBy === "team") key = issue.assignedToTeam || "Unassigned";
            else if (groupBy === "fixture") key = issue.fixtureType || "Unknown";
            else if (groupBy === "site") key = `${issue.storeName || ""} #${issue.siteId || ""}`;
            else key = "";
            if (!groups[key]) groups[key] = [];
            groups[key].push(issue);
        }
        return groups;
    }, [issues, groupBy]);

    const clearFilters = () => {
        setFilterSite(""); setFromDate(""); setToDate(""); setFilterSeverity("");
        setFilterStatus(""); setFilterTeam(""); setFilterRpl(""); setFilterState(""); setFilterFixture(""); setFilterSource("");
        setMyIssuesOnly(false); setSortBy("newest");
    };

    const title = urlStatus === "open" ? "Open Issues"
        : urlStatus === "actioned" ? "Verify Issues"
        : urlStatus === "resolved" ? "Closed Issues" : "Issues";

    return (
        <div>
            <div className="page-header">
                <h2>{title}</h2>
                <p>
                    {loading ? "Loading…" : `Showing ${issues.length.toLocaleString()} of ${totalCount.toLocaleString()} matching`}
                    {urlStatus ? "" : " across all stores"}
                </p>
            </div>

            {(filterRpl || filterState) && (
                <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Filtered to:</span>
                    {filterRpl && (
                        <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 12, background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
                            RPL: {filterRpl}
                            <span onClick={() => setFilterRpl("")} style={{ cursor: "pointer", marginLeft: 8, color: "var(--primary)", textDecoration: "underline" }}>clear</span>
                        </span>
                    )}
                    {filterState && (
                        <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 12, background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
                            State: {filterState}
                            <span onClick={() => setFilterState("")} style={{ cursor: "pointer", marginLeft: 8, color: "var(--primary)", textDecoration: "underline" }}>clear</span>
                        </span>
                    )}
                </div>
            )}

            {/* Summary stats row — overall counts (site-scoped if a site is entered) */}
            <div className="stats-grid" style={{ marginBottom: 16 }}>
                <div className="stat-card">
                    <div className="stat-label">Open</div>
                    <div className="stat-value">{counts.open.toLocaleString()}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Awaiting Verify</div>
                    <div className="stat-value">{counts.actioned.toLocaleString()}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Resolved</div>
                    <div className="stat-value">{counts.resolved.toLocaleString()}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Matching Filter</div>
                    <div className="stat-value">{totalCount.toLocaleString()}</div>
                </div>
            </div>

            {/* My Issues toggle */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <span
                    onClick={() => setMyIssuesOnly(false)}
                    style={{
                        padding: "5px 14px", fontSize: 13, borderRadius: 16, cursor: "pointer",
                        background: !myIssuesOnly ? "var(--primary)" : "var(--bg-secondary)",
                        color: !myIssuesOnly ? "#fff" : "var(--text-primary)",
                        border: "1px solid " + (!myIssuesOnly ? "var(--primary)" : "var(--border)"),
                        fontWeight: 500,
                    }}
                >
                    All Issues
                </span>
                <span
                    onClick={() => setMyIssuesOnly(true)}
                    style={{
                        padding: "5px 14px", fontSize: 13, borderRadius: 16, cursor: "pointer",
                        background: myIssuesOnly ? "var(--primary)" : "var(--bg-secondary)",
                        color: myIssuesOnly ? "#fff" : "var(--text-primary)",
                        border: "1px solid " + (myIssuesOnly ? "var(--primary)" : "var(--border)"),
                        fontWeight: 500,
                    }}
                >
                    My Issues
                </span>
            </div>

            {/* Filter row — these drive the server query (filter first, then load) */}
            <div className="filter-row" style={{ marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                <input
                    type="text"
                    className="filter-select"
                    placeholder="Site ID..."
                    value={filterSite}
                    onChange={(e) => setFilterSite(e.target.value)}
                    style={{ minWidth: 120, fontSize: 13, padding: "6px 10px" }}
                />
                <label style={{ fontSize: 12, color: "var(--text-muted)" }}>From
                    <input type="date" className="filter-select" value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)} style={{ marginLeft: 4 }} />
                </label>
                <label style={{ fontSize: 12, color: "var(--text-muted)" }}>To
                    <input type="date" className="filter-select" value={toDate}
                        onChange={(e) => setToDate(e.target.value)} style={{ marginLeft: 4 }} />
                </label>
                <select className="filter-select" value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}>
                    <option value="">All Severities</option>
                    {SEVERITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">All Statuses</option>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="filter-select" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)}>
                    <option value="">All Teams</option>
                    {teams.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select className="filter-select" value={filterRpl} onChange={(e) => setFilterRpl(e.target.value)}>
                    <option value="">All RPLs</option>
                    {rplOptions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <select className="filter-select" value={filterFixture} onChange={(e) => setFilterFixture(e.target.value)}>
                    <option value="">All Fixtures</option>
                    {fixtures.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <select className="filter-select" value={filterSource} onChange={(e) => setFilterSource(e.target.value)}>
                    <option value="">All Sources</option>
                    <option value="human">Human Created</option>
                    <option value="auto">Auto Detected</option>
                    <option value="routed">Routed</option>
                </select>
                <select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="severity">By Severity</option>
                </select>
                <select className="filter-select" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                    <option value="none">No Grouping</option>
                    <option value="team">Group by Team</option>
                    <option value="fixture">Group by Fixture</option>
                    <option value="site">Group by Site</option>
                </select>
                {!urlStatus && (
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
                        <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
                        Include Resolved
                    </label>
                )}
                <button className="filter-select" onClick={clearFilters}
                    style={{ cursor: "pointer", fontSize: 13 }}>Clear</button>
            </div>

            {error ? (
                <div className="empty-state">
                    <h3>Failed to load issues</h3>
                    <p>{error}</p>
                    <button className="retry-btn" onClick={loadFirstPage}>Retry</button>
                </div>
            ) : loading ? (
                <LoadingSpinner message="Loading issues..." />
            ) : issues.length > 0 ? (
                <div>
                    {Object.entries(grouped).map(([groupName, groupIssues]) => (
                        <div key={groupName}>
                            {groupName && (
                                <div style={{
                                    fontWeight: 600, fontSize: 15, padding: "10px 0 6px",
                                    borderBottom: "1px solid var(--border)", marginBottom: 12, marginTop: 8,
                                    color: "var(--text-primary)"
                                }}>
                                    {groupName}
                                    <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 8, fontSize: 13 }}>
                                        ({groupIssues.length})
                                    </span>
                                </div>
                            )}
                            <div className="issues-list">
                                {groupIssues.map((issue) => (
                                    <IssueCard key={issue.id} issue={issue} onAction={loadFirstPage} />
                                ))}
                            </div>
                        </div>
                    ))}

                    {hasMore && (
                        <div style={{ textAlign: "center", marginTop: 20 }}>
                            <button className="retry-btn" onClick={loadMore} disabled={loadingMore}>
                                {loadingMore ? "Loading…" : `Load more (${(totalCount - issues.length).toLocaleString()} remaining)`}
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="empty-state">
                    <h3>No issues</h3>
                    <p>No issues match the current filters.</p>
                </div>
            )}
        </div>
    );
}

export default Issues;
